import Modal from "components/modal";
import CloseIcon from "assets/icons/close.svg";
import { Button } from "components/button";
import WeightInput from "components/weight-input";
import WeightEstimation from "components/weight-estimation";
import { useState, useEffect, useContext } from "react";
import { calculateEstimate } from "lib/calculateEstimate";
import { localizeDecimalSeparator } from "lib/utils";
import { useCurrentLocation } from "lib/utils";
import { PAYMENT_ARGS } from "consts/feature-config";
import useWhen from "hooks/useWhen";
import InputValidation from "components/input-validation";
import stores from "consts/stores";
import weightRestrictions from "consts/weight-restrictions.json";
import { useUpdateTakeback } from "api/use-takebacks";
import AuthContext from "contexts/AuthContext";

const { REACT_APP_API } = process.env;
const fetchTakeback = async ({ accessToken, caseId }) => {
  try {
    const takebackRes = await fetch(`${REACT_APP_API}/takeback/${caseId}`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
    });
    if (!takebackRes.ok) throw new Error("Failed to fetch takeback");
    const takebackData = await takebackRes.json();
    let takebackObj = takebackData?.[0];

    return takebackObj; // Return the data so we can use it immediately
  } catch (error) {
    console.error(error);
    return null;
  }
};

export default function WeightModal({
  setModalOpen,
  modalOpen,
  currentWeight,
  replayWeight,
  replayDonation,
  returnMethod,
  caseId,
  onSuccess,
}) {
  const [updatedWeight, setUpdatedWeight] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const { location: currentLocation } = useCurrentLocation();
  const showPayment = useWhen(PAYMENT_ARGS);

  const { mutate: updateTakeback } = useUpdateTakeback({
    onSuccess: async () => {
      await onSuccess();
      setIsSubmitting(false);
    },
    onError: (error) => {
      console.error("Error updating takeback:", error);
      setIsSubmitting(false);
    },
  });

  useEffect(() => {
    if (currentWeight) {
      setUpdatedWeight(currentWeight.toString());
    }
  }, [currentWeight]);

  const weightDifference = parseFloat(
    updatedWeight - updatedWeight * (replayDonation / 100)
  );
  const estimate = calculateEstimate(weightDifference.toString());

  const isKiosk = returnMethod
    ? stores.some(
        (store) =>
          returnMethod.includes(store.value) && store.return_type === "kiosk"
      )
    : false;

  const maxWeight = isKiosk
    ? weightRestrictions.find((weight) => weight.unit === "lbs").maxKiosk
    : weightRestrictions.find((weight) => weight.unit === "lbs").maxMail;

  const isWeightOverLimit = parseFloat(updatedWeight) > maxWeight;
  const isWeightZero = parseFloat(updatedWeight) === 0;
  const isWeightLessThanReplay =
    parseFloat(updatedWeight) < parseFloat(replayWeight);
  const isWeightUnchanged =
    parseFloat(updatedWeight) === parseFloat(currentWeight);

  const { authTokens } = useContext(AuthContext);

  const handleConfirm = async () => {
    setIsSubmitting(true);
    setErrorMessage("");

    const fetchedData = await fetchTakeback({
      accessToken: authTokens.access_token,
      caseId,
    });

    const hasDroppedOffStatus = fetchedData?.status_history?.some(
      (status) => status.status === "dropped-off"
    );

    if (hasDroppedOffStatus) {
      setErrorMessage(
        "Sorry, you can't edit the weight after sending the trade-in"
      );
      setIsSubmitting(false);
      return;
    }

    updateTakeback({
      caseId,
      originalEstimatedWeight: parseFloat(updatedWeight),
      weight: parseFloat(updatedWeight),
      originalReplayWeight: parseFloat(updatedWeight) * (replayDonation / 100),
    });
  };

  return (
    <Modal
      setModalOpen={setModalOpen}
      modalOpen={modalOpen}
      className="flex flex-col justify-between min-h-[550px]"
      isLoading={isSubmitting}
      loadingMessage="Updating weight..."
    >
      <div className="flex flex-col gap-large py-4">
        <div className="flex justify-between mb-4">
          <h2 className="text-4xl font-semibold leading-none">Edit weight</h2>
          <div className="cursor-pointer" onClick={() => setModalOpen(false)}>
            <img src={CloseIcon} alt="" variant="secondary" />
          </div>
        </div>
        <div>
          <WeightInput
            placeholder="0.0"
            caption="Weight of Bricks*"
            unit="lbs"
            value={updatedWeight}
            onChange={(e) => setUpdatedWeight(e.target.value)}
          />
          <p className="text-sm text-tertiary mt-2">
            *The entered weight is used for the purpose of estimating the reward
            amount. The actual reward amount will be based on the actual weight
            of eligible LEGO bricks received.
          </p>
        </div>
        {(isWeightZero ||
          isWeightLessThanReplay ||
          isWeightOverLimit ||
          errorMessage) && (
          <InputValidation
            errorMessage={
              errorMessage
                ? errorMessage
                : isWeightZero
                  ? "The updated weight must be greater than 0."
                  : isWeightLessThanReplay
                    ? "Your total weight cannot be less than your donation weight. Please adjust your donation first if you'd like to reduce your total weight."
                    : `The maximum weight limit is ${maxWeight} lbs.`
            }
            iconIsInfo={!errorMessage}
          />
        )}
        {parseFloat(updatedWeight) > 0 &&
          parseFloat(updatedWeight) >= parseFloat(replayWeight) &&
          !isWeightOverLimit && (
            <WeightEstimation
              className="mt-4"
              title="Your reward will be worth"
              singleValue={
                !showPayment
                  ? localizeDecimalSeparator(estimate.gift, currentLocation)
                  : null
              }
              range={
                showPayment
                  ? [
                      localizeDecimalSeparator(
                        estimate.payment,
                        currentLocation
                      ),
                      localizeDecimalSeparator(estimate.gift, currentLocation),
                      currentLocation,
                    ]
                  : null
              }
              animate
              locale={currentLocation?.locale}
              description={`Paid out as a LEGO® e-Gift Card.`}
            />
          )}
      </div>
      <div className="flex justify-between">
        <Button
          className=""
          variant="secondary"
          onClick={() => setModalOpen(false)}
        >
          Cancel
        </Button>
        <Button
          className=""
          onClick={handleConfirm}
          disabled={
            isWeightZero ||
            isWeightLessThanReplay ||
            isWeightUnchanged ||
            isWeightOverLimit ||
            !!errorMessage
          }
        >
          Confirm
        </Button>
      </div>
    </Modal>
  );
}
